import { Link } from "react-router-dom";
import { useState } from "react";
import { Scale, Camera, Ruler, AlertCircle, MessageCircle, Fish, ShieldAlert, ExternalLink } from "lucide-react";
import PageMeta from "./PageMeta";

function SectionHeader({ icon: Icon, title, color = "cyan" }) {
  const colorClasses = {
    cyan: "bg-cyan-400/10 ring-cyan-400/20 text-cyan-300",
    violet: "bg-violet-400/10 ring-violet-400/20 text-violet-400",
    emerald: "bg-emerald-400/10 ring-emerald-400/20 text-emerald-300",
    red: "bg-red-400/10 ring-red-400/20 text-red-300",
  };

  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${colorClasses[color]} ring-1`}>
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="text-xl font-semibold text-white">{title}</h2>
    </div>
  );
}

export default function WeighMeasurePage() {
  const [imagesOpen, setImagesOpen] = useState(true);
  const [handlingOpen, setHandlingOpen] = useState(true);

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Weigh & Measure Guide | Redruth Sea Angling Association"
        description="Guidance on handling, weighing and measuring your catch correctly for Redruth Sea Angling Association competitions and records."
        keywords="how to weigh a fish, sea angling weigh in guide, RSAA weigh measure, sea fish handling, unhooking, catch and release, weever, stingray"
        url="https://www.redruthseaanglingassociation.co.uk/weigh-measure"
      />
      <div className="mx-auto max-w-3xl px-6 pt-12 pb-16 sm:px-8 lg:px-12">

        <Link
          to="/"
          className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
        >
          ← Back
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-400/10">
            <Scale className="h-6 w-6 text-cyan-300" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Catch &amp; Release</p>
            <h1 className="heading-glow mt-1 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Weighing &amp; Measuring Procedures
            </h1>
          </div>
        </div>

        <div className="mt-8 space-y-8 rounded-[2rem] border border-white/10 bg-slate-900/95 p-8 backdrop-blur panel-lift">

          <p className="text-base leading-7 text-slate-300">
            Follow these procedures to ensure fish are handled with care and accurately weighed and measured
            for Catch &amp; Release competition records.
          </p>

          {/* Weighing */}
          <section>
            <SectionHeader icon={Scale} title="Weighing the Fish" />
            <p className="text-sm leading-7 text-slate-300">
              Place the bag, container, or weigh sling onto the scales and zero (tare) them before use.
              Once the scales are correctly set, place the fish inside and record the weight.
            </p>
          </section>

          {/* Photography */}
          <section className="rounded-xl border border-cyan-300/20 bg-cyan-400/5 p-6 panel-lift">
            <SectionHeader icon={Camera} title="Photographic Evidence" />
            <p className="text-sm leading-7 text-slate-300">
              A clear photograph must be taken showing the recorded weight. Acceptable units are:
            </p>
            <ul className="mt-3 space-y-1 text-sm text-slate-300">
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" /> Pounds, ounces and drams</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" /> Grams</li>
            </ul>
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-400/20 bg-red-400/5 px-4 py-3 panel-lift">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-400 mt-0.5" />
              <p className="text-sm text-red-300">Metric pounds and ounces are <strong>not acceptable</strong>.</p>
            </div>
          </section>

          {/* Measuring */}
          <section>
            <SectionHeader icon={Ruler} title="Measuring the Fish" color="violet" />
            <p className="text-sm leading-7 text-slate-300">
              Position the measuring device alongside the fish so that the scale markings are clearly
              visible in the photograph. The fish should be straight and fully extended, with the nose touching the zero mark and the tail fin clearly visible.
            </p>

            <p className="mt-4 text-sm leading-7 text-slate-300">
              For certain fish, additional photographs are required to show specific measurement points. For example, rays must be measured across the wingspan, so a clear photo showing the full wingspan alongside the measuring device is necessary.
            </p>
            
            <p className="mt-4 text-sm leading-7 text-slate-300"> 
              Lesser-Spotted/Greater-Spotted Dogfish need to have clear photograph of the nasal flaps.
              Mullet also need additional photographs.  Please check the Club Rules for full details.
            </p>

            <button 
              type="button" 
              onClick={() => setImagesOpen((o) => !o)} 
              className="mt-4 flex items-center gap-2 rounded-lg border border-violet-300/40 bg-violet-400/10 px-3 py-2 text-sm font-semibold text-violet-300 transition hover:bg-violet-400/20"
            >
              {imagesOpen ? "Hide" : "Show"} Examples
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 transition-transform duration-300 ${imagesOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
            </button>

            {imagesOpen && (
              <>
                <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
                  <img
                    src="/assets/Tape_measure_1.webp"
                    alt="Measuring a fish with a tape measure"
                    className="w-full object-contain"
                  />
                </div>

                <p className="mt-4 text-sm leading-7 font-medium text-white">
                  Rays must be measured across the wingspan.
                </p>

                <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
                  <img
                    src="/assets/Tape_measure_2.webp"
                    alt="Measuring a ray across the wingspan"
                    className="w-full object-contain"
                  />
                </div>
              </>
            )}
          </section>

          {/* Fish handling */}
          <section className="rounded-xl border border-emerald-300/20 bg-emerald-400/5 p-6 panel-lift">
            <SectionHeader icon={Fish} title="Fish Handling Best Practice" color="emerald" />
            <p className="text-sm leading-7 text-slate-300">
              Good handling while unhooking, weighing, measuring and photographing your catch gives every fish
              the best chance of surviving after release.
            </p>

            <button
              type="button"
              onClick={() => setHandlingOpen((o) => !o)}
              className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-300/40 bg-emerald-400/10 px-3 py-2 text-sm font-semibold text-emerald-300 transition hover:bg-emerald-400/20"
            >
              {handlingOpen ? "Hide" : "Show"} Handling Guide
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 transition-transform duration-300 ${handlingOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
            </button>

            {handlingOpen && (
              <div className="mt-6 space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-white">Handling</h3>
                  <ul className="mt-2 space-y-1.5 text-sm leading-6 text-slate-300">
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Only touch fish with wet hands or a wet towel. Dry hands and cloths strip the protective slime and scales.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Keep the fish out of the water for as short a time as possible, and pour cool, fresh sea water over it while unhooking, especially on warm days.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Measure on a wet, padded unhooking mat and weigh in a wet sling that fully supports the fish.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Never lift a fish by its gills, spiracles or tail.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Use a drop net or landing net, never a gaff.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Hold the fish low over the mat or ground for photos, so it has no distance to fall if dropped.</span></li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-white">Unhooking</h3>
                  <ul className="mt-2 space-y-1.5 text-sm leading-6 text-slate-300">
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Carry the right disgorgers, long-nosed pliers and T-bars for the species and hook sizes you are fishing for.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Consider circle hooks to reduce deep hooking, swap trebles on lures for single hooks, and crush barbs on hooks for larger fish.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Never pull or force a hook out. If a deep hook cannot be removed safely, cut the line as close to the hook as you can.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Avoid going in through the gills unless you are experienced, as gills are easily damaged.</span></li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-white">Releasing</h3>
                  <ul className="mt-2 space-y-1.5 text-sm leading-6 text-slate-300">
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Never throw a fish back. Hold it in the water until it has the strength to swim away on its own.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>From a boat, support the fish in a net at the side while it recovers, or use a live well for smaller fish.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Fish brought up from depth may show barotrauma (raised scales, bulging eyes or a swollen swim bladder). A fish descender returns them to depth and gives them a good chance of survival.</span></li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-white">Sharks, Skates &amp; Rays</h3>
                  <ul className="mt-2 space-y-1.5 text-sm leading-6 text-slate-300">
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Never lay a skate or ray on its back to unhook it, as this removes its protective slime.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Keep fingers well away from the mouth, as the crushing plates can cause serious injury.</span></li>
                  <li className="flex items-start gap-2"><span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /><span>Don't lift large sharks out of the water unless absolutely necessary. Use wire cutters if a hook can't be removed safely.</span></li>
                  </ul>
                </div>

                <div className="rounded-lg border border-red-400/20 bg-red-400/5 p-4 panel-lift">
                  <SectionHeader icon={ShieldAlert} title="Venomous Species" color="red" />
                  <ul className="space-y-1.5 text-sm leading-6 text-slate-300">
                    <li><strong className="text-white">Weever fish:</strong> the black spine on the first dorsal fin is venomous and very painful. Unhook small weevers with a disgorger while still in the water, or use fish grips, keeping clear of the spine.</li>
                    <li><strong className="text-white">Stingray:</strong> the barb sits midway along the tail, and the ray can arch its tail over its back, so it can strike from any angle. Wrap the tail in a thick, wet towel before unhooking, and get medical attention straight away if you are stung.</li>
                  </ul>
                </div>

                <p className="text-xs leading-6 text-slate-400">
                  Adapted from{" "}
                  <a
                    href="https://youranglingvoice.co.uk/home/the-ultimate-sea-fish-handling-guide-for-uk-anglers/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-cyan-400 underline transition hover:text-cyan-300"
                  >
                    The Ultimate Sea Fish Handling Guide for UK Anglers <ExternalLink className="h-3 w-3" />
                  </a>{" "}
                  by Your Angling Voice. See also the{" "}
                  <a href="https://www.sharktrust.org" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline transition hover:text-cyan-300">Shark Trust</a>{" "}
                  for shark, skate and ray handling advice.
                </p>
              </div>
            )}
          </section>

          {/* Submission */}
          <section className="rounded-xl border border-amber-300/20 bg-amber-400/5 p-6 panel-lift">
            <h2 className="text-xl font-semibold text-white mb-3">Submitting Your Catch</h2>
            <p className="text-sm leading-7 text-slate-300">
              Photographs must be submitted to the <strong className="text-white">Fish Recorder (Chris Wills)</strong>{" "}
              (
              <a
                href="https://wa.me/447771646872"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-cyan-400 underline transition hover:text-cyan-300"
              >
                <MessageCircle className="h-3.5 w-3.5" /> Contact via WhatsApp
              </a>
              ) <strong className="text-white">- Sea View, Treskerby, Redruth, TR15 1RG</strong> or the
              <strong className="text-white"> Secondary Fish Recorder (Brian Collick) 01209 217617 / 07759 332750 - 4 Agar Road, Illogan Highway, TR15 3NA</strong>, together with written confirmation of the fish's weight.
            </p>
            
            <p className="mt-4 text-sm leading-7 text-slate-300">
              All details need to be submitted within 48 hours of the catch, and the fish must be released alive after weighing and measuring.
            </p>
          </section>

          {/* Scales policy reminder */}
          <section>
            <p className="text-sm leading-7 text-slate-300">
              Members are also required to refer to the{" "}
              <Link to="/scales-policy" className="text-cyan-400 underline hover:text-cyan-300 transition">
                Members' Scale Policy
              </Link>{" "}
              on this website. This contains important information to ensure full compliance, including
              confirmation that scales used are suitable and have been accuracy-checked by the club's recorder.
            </p>
          </section>

          <div className="mt-8 flex justify-end">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-300/30 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/20"
            >
              ↑ Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
